/**
 * 埋点核心逻辑
 */

import type {
  EventType,
  PageStayData,
  TrackingData,
  TrackingEventData,
  TrackingOptions,
} from './types';

import { LANDING_VISIT } from './constants';

/** 默认配置 */
const defaultOptions: Required<TrackingOptions> = {
  reportUrl: import.meta.env.VITE_GLOB_REPORT_URL,
  debug: false,
  batchSize: 10,
  reportInterval: 5000,
  enableStorage: true,
  storageKey: 'tracking_queue',
};

/** traceId 本地存储 key */
const TRACE_ID_KEY = 'tracking_trace_id';

/** 当前配置 */
let currentOptions: Required<TrackingOptions> = { ...defaultOptions };

/** 页面停留状态 */
const pageStayState = {
  enterTime: '',
  hasInteraction: false,
  currentPageUrl: '',
  initTimestamp: 0,
  delayMs: 3000, // 3秒延迟，用于落地页访问判断
  isReported: false, // 是否已上报，避免重复
};

/** 3秒定时器 */
let landingVisitTimer: ReturnType<typeof setTimeout> | null = null;

/** 初始化页面停留追踪 */
export function initPageStayTracking(): void {
  // 清除之前的定时器
  if (landingVisitTimer) {
    clearTimeout(landingVisitTimer);
    landingVisitTimer = null;
  }

  pageStayState.enterTime = getCurrentTime();
  pageStayState.hasInteraction = false;
  pageStayState.currentPageUrl = getCurrentPageUrl();
  pageStayState.initTimestamp = Date.now();
  pageStayState.isReported = false;

  if (currentOptions.debug) {
    console.log('[Tracking] 初始化页面停留追踪:', pageStayState.enterTime);
  }

  // 设置3秒定时器，到时自动上报
  landingVisitTimer = setTimeout(() => {
    if (!pageStayState.isReported) {
      reportLandingVisit(true); // 3秒到时上报
    }
  }, pageStayState.delayMs);
}

/** 标记有用户交互 */
export function markUserAction(): void {
  const elapsed = Date.now() - pageStayState.initTimestamp;

  // 3秒后的交互才标记并上报
  if (elapsed >= pageStayState.delayMs && !pageStayState.hasInteraction) {
    pageStayState.hasInteraction = true;
    if (currentOptions.debug) {
      console.log('[Tracking] 标记有用户交互， elapsed:', elapsed, 'ms');
    }
    // 有交互时立即上报
    if (!pageStayState.isReported) {
      reportLandingVisit(false); // 交互触发上报
    }
  }
}

/**
 * 上报落地页访问事件
 * @param isTimeout 是否由3秒定时器触发
 */
function reportLandingVisit(isTimeout: boolean): void {
  // 避免重复上报
  if (pageStayState.isReported) {
    return;
  }

  const elapsed = Date.now() - pageStayState.initTimestamp;
  const leaveTime = getCurrentTime();

  const trackingData: TrackingData = {
    eventId: generateUUID(),
    traceId: currentTraceId,
    eventType: LANDING_VISIT,
    eventTime: leaveTime,
    pageUrl: pageStayState.currentPageUrl,
    elementId: null,
    elementDesc: null,
    clientId: getClientId(),
    data: {
      enterTime: pageStayState.enterTime,
      leaveTime: leaveTime,
      duration: elapsed,
      pageUrl: pageStayState.currentPageUrl,
      hasInteraction: pageStayState.hasInteraction,
      triggerType: isTimeout ? 'timeout' : 'interaction', // 上报触发类型
    },
  };

  if (currentOptions.debug) {
    console.log('[Tracking] 上报落地页访问事件:', trackingData);
  }

  // 标记已上报
  pageStayState.isReported = true;

  // 使用 sendBeacon 上报
  try {
    const blob = new Blob([JSON.stringify(trackingData)], {
      type: 'application/json',
    });
    const result = navigator.sendBeacon?.(currentOptions.reportUrl, blob);
    if (currentOptions.debug) {
      console.log('[Tracking] 落地页访问上报结果:', result);
    }
  } catch (error) {
    console.error('[Tracking] 落地页访问上报失败:', error);
  }
}

/** 页面关闭前上报落地页访问事件（兼容旧逻辑，但不再重复上报） */
export function reportPageStayBeforeUnload(): void {
  // 如果已经上报过，不再重复上报
  if (pageStayState.isReported) {
    if (currentOptions.debug) {
      console.log('[Tracking] 落地页访问已上报，不再重复上报');
    }
    return;
  }

  const elapsed = Date.now() - pageStayState.initTimestamp;

  // 落地页访问条件：停留时长>3秒 或 触发页面交互
  if (elapsed < pageStayState.delayMs && !pageStayState.hasInteraction) {
    if (currentOptions.debug) {
      console.log('[Tracking] 不满足落地页访问条件，不上报');
    }
    return;
  }

  // 满足条件但未上报时，在页面关闭前上报
  reportLandingVisit(false);
}

/** 上报队列 */
let reportQueue: TrackingData[] = [];

/** 定时器 */
let reportTimer: ReturnType<typeof setInterval> | null = null;

/** 当前 traceId */
let currentTraceId: string = '';

/**
 * 生成 UUID
 */
function generateUUID(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * 获取当前时间字符串
 * 格式: yyyy-MM-dd HH:mm:ss.SSS
 */
function getCurrentTime(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');
  const seconds = String(now.getSeconds()).padStart(2, '0');
  const milliseconds = String(now.getMilliseconds()).padStart(3, '0');

  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}.${milliseconds}`;
}

/**
 * 获取当前页面路由（hash 模式）
 * 返回 # 后面的路径，如：/#/color-card/upload 返回 /color-card/upload
 */
function getCurrentPageUrl(): string {
  if (typeof window === 'undefined') {
    return '';
  }
  // 获取 hash 路由，去掉开头的 #
  const hash = window.location.hash;
  if (hash) {
    return hash.replace(/^#/, '');
  }
  // 如果没有 hash，返回 pathname
  return window.location.pathname;
}

/**
 * 获取客户端ID
 * 根据窗口宽度判断是PC端还是移动端
 */
function getClientId(): string {
  if (typeof window === 'undefined') {
    return import.meta.env.VITE_GLOB_APP_CLIENT_ID || '';
  }
  // 窗口宽度小于768认为是移动端
  if (window.innerWidth < 768) {
    return import.meta.env.VITE_GLOB_APP_CLIENT_ID_MOBILE || '';
  }
  return import.meta.env.VITE_GLOB_APP_CLIENT_ID || '';
}

/**
 * 从本地存储加载队列
 */
function loadQueueFromStorage(): void {
  if (!currentOptions.enableStorage || typeof window === 'undefined') {
    return;
  }

  try {
    const stored = localStorage.getItem(currentOptions.storageKey);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed)) {
        reportQueue = parsed;
        if (currentOptions.debug) {
          console.log('[Tracking] 从本地存储加载队列:', reportQueue.length);
        }
      }
    }
  } catch (error) {
    console.error('[Tracking] 加载本地存储队列失败:', error);
  }
}

/**
 * 保存队列到本地存储
 */
function saveQueueToStorage(): void {
  if (!currentOptions.enableStorage || typeof window === 'undefined') {
    return;
  }

  try {
    localStorage.setItem(
      currentOptions.storageKey,
      JSON.stringify(reportQueue),
    );
  } catch (error) {
    console.error('[Tracking] 保存队列到本地存储失败:', error);
  }
}

/**
 * 使用 sendBeacon 上报数据
 * 适用于页面关闭等不可靠发送场景
 */
function sendBeaconReport(data: TrackingData[]): boolean {
  if (typeof navigator === 'undefined' || !navigator.sendBeacon) {
    return false;
  }

  if (data.length === 0) {
    return true;
  }

  try {
    // 单条数据直接上报对象，多条数据上报数组
    const body = data.length === 1 ? data[0] : data;
    const blob = new Blob([JSON.stringify(body)], {
      type: 'application/json',
    });
    const result = navigator.sendBeacon(currentOptions.reportUrl, blob);

    if (currentOptions.debug) {
      if (result) {
        console.log('[Tracking] sendBeacon 上报成功:', data.length, '条数据');
      } else {
        console.warn('[Tracking] sendBeacon 上报失败，可能被浏览器阻止');
      }
    }

    return result;
  } catch (error) {
    console.error('[Tracking] sendBeacon 上报异常:', error);
    return false;
  }
}

/**
 * 使用 fetch 上报数据
 * 适用于正常场景，支持错误处理和重试
 * 不需要请求头
 */
async function fetchReport(data: TrackingData[]): Promise<boolean> {
  if (data.length === 0) {
    return true;
  }

  if (currentOptions.debug) {
    console.log('[Tracking] fetch 上报数据:', data);
  }

  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);

    // 单条数据直接上报对象
    const body = data.length === 1 ? data[0] : data;

    const response = await fetch(currentOptions.reportUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
      signal: controller.signal,
      keepalive: true,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    if (currentOptions.debug) {
      console.log('[Tracking] fetch 上报成功');
    }

    return true;
  } catch (error) {
    console.error('[Tracking] fetch 上报失败:', error);
    return false;
  }
}

/**
 * 上报数据（自动选择最佳方式）
 * 优先使用 fetch，失败时尝试 sendBeacon
 */
async function doReport(data: TrackingData[]): Promise<void> {
  if (data.length === 0) {
    return;
  }

  // 优先使用 fetch 上报
  const fetchSuccess = await fetchReport(data);

  if (fetchSuccess) {
    return;
  }

  // fetch 失败，尝试 sendBeacon
  if (currentOptions.debug) {
    console.log('[Tracking] fetch 失败，尝试 sendBeacon');
  }

  const beaconSuccess = sendBeaconReport(data);

  if (!beaconSuccess) {
    // 都失败了，数据放回队列
    reportQueue.unshift(...data);
    saveQueueToStorage();
  }
}

/**
 * 批量上报
 */
function batchReport(): void {
  if (reportQueue.length === 0) {
    return;
  }

  const batch = reportQueue.splice(0, currentOptions.batchSize);
  saveQueueToStorage();
  void doReport(batch);
}

/**
 * 页面关闭前上报剩余数据
 * 优先使用 sendBeacon，失败时使用 fetch with keepalive
 */
function reportBeforeUnload(): void {
  if (reportQueue.length === 0) {
    return;
  }

  const remainingData = [...reportQueue];
  reportQueue = [];
  saveQueueToStorage();

  if (currentOptions.debug) {
    console.log('[Tracking] 页面关闭前上报:', remainingData.length, '条数据');
  }

  // 优先使用 sendBeacon
  const beaconSuccess = sendBeaconReport(remainingData);

  if (!beaconSuccess) {
    // sendBeacon 失败，尝试 fetch with keepalive
    if (currentOptions.debug) {
      console.log('[Tracking] sendBeacon 失败，尝试 fetch keepalive');
    }

    void fetchReport(remainingData).then((success) => {
      if (!success) {
        // 都失败了，数据恢复
        reportQueue.unshift(...remainingData);
        saveQueueToStorage();
      }
    });
  }
}

/**
 * 初始化埋点系统
 */
export function initTracking(options?: TrackingOptions): void {
  currentOptions = {
    ...defaultOptions,
    ...options,
  };

  // 加载本地存储的队列
  loadQueueFromStorage();

  // 加载本地存储的 traceId
  loadTraceIdFromStorage();

  // 初始化页面停留追踪
  initPageStayTracking();

  // 启动定时上报
  if (reportTimer) {
    clearInterval(reportTimer);
  }

  reportTimer = setInterval(() => {
    batchReport();
  }, currentOptions.reportInterval);

  // 页面关闭前上报剩余数据
  if (typeof window !== 'undefined') {
    window.addEventListener('beforeunload', () => {
      reportBeforeUnload();
      reportPageStayBeforeUnload();
    });

    // 页面可见性变化时上报剩余数据（切换标签页）
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        reportBeforeUnload();
        reportPageStayBeforeUnload();
      }
    });
  }

  if (currentOptions.debug) {
    console.log('[Tracking] 初始化完成', currentOptions);
  }
}

/**
 * 从本地存储加载 traceId
 */
function loadTraceIdFromStorage(): void {
  if (!currentOptions.enableStorage || typeof window === 'undefined') {
    return;
  }

  try {
    const stored = localStorage.getItem(TRACE_ID_KEY);
    if (stored) {
      currentTraceId = stored;
      if (currentOptions.debug) {
        console.log('[Tracking] 从本地存储加载 traceId:', stored);
      }
    }
  } catch (error) {
    console.error('[Tracking] 加载 traceId 失败:', error);
  }
}

/**
 * 保存 traceId 到本地存储
 */
function saveTraceIdToStorage(traceId: string): void {
  if (!currentOptions.enableStorage || typeof window === 'undefined') {
    return;
  }

  try {
    localStorage.setItem(TRACE_ID_KEY, traceId);
  } catch (error) {
    console.error('[Tracking] 保存 traceId 失败:', error);
  }
}

/**
 * 清除本地存储的 traceId
 */
function clearTraceIdFromStorage(): void {
  if (!currentOptions.enableStorage || typeof window === 'undefined') {
    return;
  }

  try {
    localStorage.removeItem(TRACE_ID_KEY);
  } catch (error) {
    console.error('[Tracking] 清除 traceId 失败:', error);
  }
}

/**
 * 设置 traceId
 * 会同时保存到内存和本地存储，刷新后仍然有效
 */
export function setTraceId(traceId: string): void {
  currentTraceId = traceId;
  saveTraceIdToStorage(traceId);
  if (currentOptions.debug) {
    console.log('[Tracking] 设置 traceId:', traceId);
  }
}

/**
 * 清除 traceId
 * 同时清除内存和本地存储
 */
export function clearTraceId(): void {
  currentTraceId = '';
  clearTraceIdFromStorage();
  if (currentOptions.debug) {
    console.log('[Tracking] 清除 traceId');
  }
}

/**
 * 上报埋点数据
 */
export function track(
  eventType: EventType,
  eventData?: TrackingEventData,
): void {
  const trackingData: TrackingData = {
    eventId: generateUUID(),
    traceId: currentTraceId,
    eventType,
    eventTime: getCurrentTime(),
    pageUrl: getCurrentPageUrl(),
    elementId: eventData?.elementId ?? null,
    elementDesc: eventData?.elementDesc ?? null,
    clientId: getClientId(),
    data: eventData?.data,
  };

  if (currentOptions.debug) {
    console.log('[Tracking] 收集事件:', trackingData);
  }

  reportQueue.push(trackingData);
  saveQueueToStorage();

  // 达到批量阈值立即上报
  if (reportQueue.length >= currentOptions.batchSize) {
    batchReport();
  }
}

/**
 * 上报页面停留事件
 */
export function trackPageStay(pageStayData: PageStayData): void {
  track(LANDING_VISIT, {
    data: {
      enterTime: pageStayData.enterTime,
      leaveTime: pageStayData.leaveTime,
    },
  });
}

/**
 * 使用 sendBeacon 立即上报单条事件
 * 适用于需要立即上报的场景（如登录）
 */
export function trackImmediate(
  eventType: EventType,
  eventData?: TrackingEventData,
): boolean {
  console.log('[Tracking] 立即上报事件:', eventType, eventData);
  const trackingData: TrackingData = {
    eventId: generateUUID(),
    traceId: currentTraceId,
    eventType,
    eventTime: getCurrentTime(),
    pageUrl: getCurrentPageUrl(),
    elementId: eventData?.elementId ?? null,
    elementDesc: eventData?.elementDesc ?? null,
    channelCode: eventData?.channelCode ?? null,
    clientId: getClientId(),
    data: eventData?.data,
  };

  if (currentOptions.debug) {
    console.log('[Tracking] 立即上报事件:', trackingData);
  }

  return sendBeaconReport([trackingData]);
}

/**
 * 销毁埋点系统
 */
export function destroyTracking(): void {
  if (reportTimer) {
    clearInterval(reportTimer);
    reportTimer = null;
  }

  // 清除落地页访问定时器
  if (landingVisitTimer) {
    clearTimeout(landingVisitTimer);
    landingVisitTimer = null;
  }

  // 移除事件监听
  if (typeof window !== 'undefined') {
    window.removeEventListener('beforeunload', reportBeforeUnload);
    document.removeEventListener('visibilitychange', reportBeforeUnload);
  }

  // 上报剩余数据
  batchReport();
}
